/**
 * Stock route registration — composition shell wires URL params/search to
 * StockRoute (plan §3.4: router.tsx is not edited in 5/6/7).
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';
import { z } from 'zod';

import {
  RequestedTickerSchema,
  StockSearchParamsSchema,
  type SectorId,
  type StockSearchParams,
  type Ticker,
  type WindowKey,
} from '@meridian/contracts';

import { EmptyState } from '@/components/primitives/empty-state';
import { Skeleton } from '@/components/primitives/skeleton';
import { rootRoute } from '@/router-root';

import { InvalidTickerParamError, isInvalidTickerParamError } from './invalid-ticker-param-error';

const loadStockRoute = () => import('./stock-route');

const StockScreen = lazy(async () => {
  const module = await loadStockRoute();
  return { default: module.StockRoute };
});

const paramsSchema = z.object({
  ticker: RequestedTickerSchema,
});

/** Sector detail navigation target — preserves win/idx from the stock screen. */
export function sectorDetailTarget(
  sectorId: SectorId,
  search: Pick<StockSearchParams, 'win' | 'idx'>,
): {
  readonly to: '/sector/$sectorId';
  readonly params: { readonly sectorId: SectorId };
  readonly search: {
    readonly win: StockSearchParams['win'];
    readonly idx: StockSearchParams['idx'];
  };
} {
  return {
    to: '/sector/$sectorId',
    params: { sectorId },
    search: { win: search.win, idx: search.idx },
  };
}

/** Peer / diversifier ticker navigation — preserves win/idx; clears chart range. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<StockSearchParams, 'win' | 'idx'>,
): {
  readonly to: '/stock/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: {
    readonly win: StockSearchParams['win'];
    readonly idx: StockSearchParams['idx'];
  };
} {
  return {
    to: '/stock/$ticker',
    params: { ticker },
    search: { win: search.win, idx: search.idx },
  };
}

function StockRouteAdapter(): JSX.Element {
  const { ticker } = stockRoute.useParams();
  const search = stockRoute.useSearch();
  const navigate = useNavigate({ from: stockRoute.fullPath });

  const onRangeChange = useCallback(
    (range: WindowKey) => {
      void navigate({
        search: (prev) => ({ ...prev, range }),
        replace: true,
      });
    },
    [navigate],
  );

  const onTickerSelect = useCallback(
    (next: Ticker) => {
      void navigate(stockDetailTarget(next, { win: search.win, idx: search.idx }));
    },
    [navigate, search.win, search.idx],
  );

  const onSectorSelect = useCallback(
    (sc: SectorId) => {
      void navigate(sectorDetailTarget(sc, { win: search.win, idx: search.idx }));
    },
    [navigate, search.win, search.idx],
  );

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading stock" />}>
      <StockScreen
        ticker={ticker}
        window={search.win}
        range={search.range}
        onRangeChange={onRangeChange}
        onTickerSelect={onTickerSelect}
        onSectorSelect={onSectorSelect}
      />
    </Suspense>
  );
}

/**
 * The CatchBoundary receives BOTH `params.parse` failures and render-time
 * exceptions from the route subtree, so it must discriminate: reporting a crash
 * inside a panel as "unknown ticker" is a wrong answer that reads as a right one.
 */
function StockRouteError({ error }: { readonly error: unknown }): JSX.Element {
  if (isInvalidTickerParamError(error)) {
    return (
      <EmptyState title="Security not found" description="Invalid or unknown ticker in the URL." />
    );
  }
  return (
    <EmptyState
      title="Something went wrong"
      description="This screen could not be displayed. Reload the page to try again."
    />
  );
}

export const stockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stock/$ticker',
  params: {
    parse: (raw) => {
      const result = paramsSchema.safeParse({ ticker: raw.ticker });
      if (!result.success) {
        /* Carries the sentinel the error boundary discriminates on. */
        throw new InvalidTickerParamError();
      }
      return result.data;
    },
    stringify: (p) => ({ ticker: p.ticker }),
  },
  validateSearch: (search: Record<string, unknown>) => StockSearchParamsSchema.parse(search),
  loader: loadStockRoute,
  component: StockRouteAdapter,
  errorComponent: StockRouteError,
});
